import { Flex, Link } from '@iheartjane/reefer';
import type {
  CategoryImageKey,
  CategoryImagesConfig,
  CategoryTabStyle,
  MenuTab,
} from '@iheartjane/types';
import { clsx } from 'clsx';
import { useEffect, useRef } from 'react';
import { hex } from 'wcag-contrast';

import { CategoryTab, getCategoryTabLabel } from './categoryTab/categoryTab';

import styles from './categoryTabs.module.css';

/**
 * Container name used by CategoryTabs for responsive container queries.
 * Set this as `containerName` with `containerType: 'inline-size'` on a
 * parent element to control responsive styles based on container width
 * instead of viewport width.
 */
export const CATEGORY_TABS_CONTAINER_NAME = 'category-tabs';

export interface CategoryTabsProps {
  categories: MenuTab[];
  customImages?: CategoryImagesConfig;
  customLabels?: Record<string, string>;
  getUrlString: (category: MenuTab) => string;
  onClick?: (category: MenuTab) => void;
  // Used for skeleton previews in Biz Admin
  preview?: boolean;
  primaryColor: string;
  selectedCategory: MenuTab;
  storeState?: string;
  variant: CategoryTabStyle;
}

export const CategoryTabs = ({
  categories,
  customImages,
  customLabels,
  onClick,
  getUrlString,
  preview,
  primaryColor,
  selectedCategory,
  storeState,
  variant = 'icon',
}: CategoryTabsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDarkPrimaryColor = hex(primaryColor, '#ffffff') >= 3;

  useEffect(() => {
    const scrollContainer = containerRef.current;
    const selectedElement = scrollContainer?.querySelector<HTMLElement>(
      '[data-selected="true"]'
    );

    if (selectedElement && scrollContainer) {
      const { offsetLeft, offsetWidth: elementWidth } = selectedElement;
      const { offsetWidth: containerWidth } = scrollContainer;

      scrollContainer.scrollTo({
        behavior: 'smooth',
        left: offsetLeft - (containerWidth - elementWidth) / 2,
      });
    }
  }, [selectedCategory]);

  return (
    <Flex
      ref={containerRef}
      className={clsx(styles['scroll'])}
      overflowX="scroll"
    >
      <div className={clsx(styles['track'], styles[`${variant}`])}>
        {categories.map((category) => {
          const label = getCategoryTabLabel(category, customLabels?.[category]);

          return (
            <Link
              ariaLabel={`${label} tab`}
              className={clsx(
                styles['link'],
                category !== selectedCategory ? styles['unselected'] : undefined
              )}
              variant="minimal"
              to={getUrlString(category)}
              key={category}
              onClick={() => onClick?.(category)}
            >
              <div data-selected={category === selectedCategory}>
                <CategoryTab
                  preview={preview}
                  darkPrimaryColor={isDarkPrimaryColor}
                  name={category}
                  customImage={customImages?.[category as CategoryImageKey]}
                  customLabel={customLabels?.[category]}
                  selected={category === selectedCategory}
                  storeState={storeState}
                  variant={variant}
                />
              </div>
            </Link>
          );
        })}
      </div>
    </Flex>
  );
};
